import type { NoteDetail } from '@brain/contracts'
import { Button, Chip, cn, EmptyState, Segmented, toast } from '@brain/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  ChevronRight,
  Cloud,
  CloudOff,
  Download,
  FilePlus,
  FileText,
  FolderInput,
  Lock,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  Pin,
  RefreshCw,
  Star,
  Trash2,
} from 'lucide-react'
import { DropdownMenu } from 'radix-ui'
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ContextPanel } from '../../components/ContextPanel.tsx'
import type { NoteEditorHandle, SyncStatus } from '../../components/editor/NoteEditor.tsx'
import { NoteList } from '../../components/NoteList.tsx'
import { BacklinksPanel } from '../../components/note/BacklinksPanel.tsx'
import { FindBar } from '../../components/note/FindBar.tsx'
import { NoteTasks } from '../../components/note/NoteTasks.tsx'
import { useCreateSubpage } from '../../components/note/PageTree.tsx'
import { type Properties, PropertiesHeader } from '../../components/note/PropertiesHeader.tsx'
import { ThreeCol } from '../../components/ThreeCol.tsx'
import { BRIEF_MAX_LINES, briefLines } from '../../lib/brief.ts'
import { downloadBlob } from '../../lib/download.ts'
import {
  cachedNoteContainer,
  containerQuery,
  noteQuery,
  notesQuery,
  useFavorites,
  useNoteAction,
  useToggleFavorite,
  useUpdateNote,
} from '../../lib/hooks.ts'
import { useKeydown } from '../../lib/shortcuts.ts'
import { pickContainer, setUi, useCurrentContainer, useCurrentNote, useUi } from '../../lib/ui-state.ts'

const NoteEditor = lazy(() => import('../../components/editor/NoteEditor.tsx'))
const VersionsPanel = lazy(() => import('../../components/note/VersionsPanel.tsx'))

export const Route = createFileRoute('/_app/n/$noteId')({ component: NotePage })

function NotePage() {
  const { t } = useTranslation()
  const { noteId } = Route.useParams()
  const queryClient = useQueryClient()
  const q = useQuery(noteQuery(noteId))
  const note = q.data
  // until the note has loaded, its container comes from the page list it was clicked in – otherwise the list column
  // would flash the inbox before the page tree comes back
  const containerId = note ? note.containerId : cachedNoteContainer(queryClient, noteId)
  const container = useQuery({ ...containerQuery(containerId ?? ''), enabled: Boolean(containerId) && !note })
  useCurrentContainer(containerId)
  useCurrentNote(note && !note.deletedAt ? note.id : null)
  const containerTitle = note ? note.container?.title : container.data?.title

  return (
    <ThreeCol
      list={
        <NoteList
          containerId={containerId}
          title={containerId ? (containerTitle ?? '') : t('nav.inbox')}
          activeId={noteId}
          back={containerId ? { to: `/c/${containerId}`, label: t('note.toContainer') } : undefined}
        />
      }
    >
      {q.isError ? (
        <div className="flex flex-1 items-center justify-center">
          <EmptyState title={t('note.notFound')} description={t('note.notFoundText')} />
        </div>
      ) : !note ? (
        <p className="p-8 text-[13px] text-meta">{t('common.loading')}</p>
      ) : (
        <NoteView key={note.id} note={note} />
      )}
    </ThreeCol>
  )
}

function useDebounced<T>(fn: (v: T) => void, ms: number) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latest = useRef(fn)
  latest.current = fn
  return useCallback(
    (v: T) => {
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => latest.current(v), ms)
    },
    [ms],
  )
}

/**
 * False for the first render, true once the browser has painted it. Building the editor takes a few hundred
 * milliseconds; done in the same render as the click, the old page would stay frozen on screen until it is finished.
 */
function useAfterPaint() {
  const [ready, setReady] = useState(false)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const frame = requestAnimationFrame(() => {
      timer = setTimeout(() => setReady(true))
    })
    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(timer)
    }
  }, [])
  return ready
}

function NoteView({ note }: { note: NoteDetail }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { me } = Route.useRouteContext()
  const update = useUpdateNote(note.id)
  const action = useNoteAction(note.id)
  const favorites = useFavorites()
  const toggleFav = useToggleFavorite()
  const focus = useUi((s) => s.focusMode)
  const [title, setTitle] = useState(note.title)
  const [status, setStatus] = useState<SyncStatus>('connecting')
  const [panel, setPanel] = useState<'backlinks' | 'versions'>('backlinks')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const saveTitle = useDebounced((v: string) => update.mutate({ title: v }), 500)
  const isFav = (favorites.data ?? []).some((f) => f.entityId === note.id)
  const deleted = Boolean(note.deletedAt)
  const editorReady = useAfterPaint()
  const editorBox = useRef<HTMLDivElement>(null)
  const editorHandle = useRef<NoteEditorHandle | null>(null)
  const [find, setFind] = useState<{ seed: string; key: number } | null>(null)

  // ⌘F searches the note (also in the Mac app, whose web view has no find bar of its own)
  useKeydown((e) => {
    if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey || e.key.toLowerCase() !== 'f') return
    e.preventDefault()
    const sel = window.getSelection()
    const inEditor = sel && editorBox.current?.contains(sel.anchorNode)
    const text = inEditor ? sel.toString().trim() : ''
    setFind((f) => ({ seed: text.length <= 100 && !text.includes('\n') ? text : '', key: (f?.key ?? 0) + 1 }))
  }, [])

  // server changes to the title reach the field unless it is being edited (a save response arriving while typing
  // would otherwise cut off what was typed since)
  const titleRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (document.activeElement !== titleRef.current) setTitle(note.title)
  }, [note.title])

  const move = async () => {
    const target = await pickContainer()
    if (target === undefined) return
    action.mutate(
      { kind: 'move', containerId: target },
      {
        onSuccess: () => toast(t('note.moved')),
        onError: () => toast(t('common.error'), { tone: 'attention' }),
      },
    )
  }

  const exportMd = () => {
    const body = `# ${note.title || t('note.untitled')}\n\n${note.contentMd}`
    void downloadBlob(
      new Blob([body], { type: 'text/markdown;charset=utf-8' }),
      `${slugify(note.title) || 'notiz'}.md`,
    )
  }

  return (
    <div className="flex flex-1 flex-col">
      <div data-tauri-drag-region className="flex h-12 shrink-0 items-center gap-2 px-4 md:px-6">
        <Link
          to={note.container ? '/c/$containerId' : '/inbox'}
          params={note.container ? { containerId: note.container.id } : {}}
          className="flex min-w-0 items-center gap-1.5 text-[12px] text-meta hover:text-text-1 md:hidden"
        >
          <ArrowLeft size={14} />
          {note.container?.title ?? t('nav.inbox')}
        </Link>
        <Breadcrumbs note={note} />
        <span className="flex-1" />
        <span
          className={cn(
            'flex items-center gap-1 text-[11px]',
            status === 'offline' ? 'text-apricot-fg' : 'text-meta',
          )}
          title={t(`sync.${status}`)}
        >
          {status === 'offline' ? (
            <CloudOff size={14} />
          ) : status === 'synced' ? (
            <Cloud size={14} />
          ) : (
            <RefreshCw size={13} />
          )}
          <span className="hidden sm:inline">{t(`sync.${status}`)}</span>
        </span>
        {!note.writable && (
          <Chip>
            <Lock size={12} />
            {t('note.readOnly')}
          </Chip>
        )}
        <Button
          size={32}
          variant="ghost"
          aria-pressed={isFav}
          aria-label={t(isFav ? 'favorites.remove' : 'favorites.add')}
          onClick={() => toggleFav.mutate({ entityType: 'note', entityId: note.id, on: !isFav })}
        >
          <Star size={16} className={cn(isFav && 'fill-current text-pop')} />
        </Button>
        <Button
          size={32}
          variant="ghost"
          aria-label={t('note.focus')}
          title={`${t('note.focus')} (⌘.)`}
          onClick={() => setUi((s) => ({ focusMode: !s.focusMode }))}
        >
          {focus ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </Button>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button size={32} variant="ghost" aria-label={t('common.more')}>
              <MoreHorizontal size={16} />
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="z-50 min-w-52 rounded-card border border-line-1 bg-card p-1.5 shadow-2"
            >
              {note.writable && (
                <Item icon={FolderInput} label={t('note.move')} onSelect={() => void move()} />
              )}
              {note.writable && (
                <Item
                  icon={Pin}
                  label={note.pinned ? t('note.unpin') : t('note.pin')}
                  onSelect={() => update.mutate({ pinned: !note.pinned })}
                />
              )}
              <Item icon={Download} label={t('note.exportMd')} onSelect={exportMd} />
              {note.writable && (
                <Item icon={Trash2} label={t('note.delete')} danger onSelect={() => setConfirmDelete(true)} />
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>

      {deleted && (
        <div className="mx-4 flex items-center gap-3 rounded-tile bg-apricot-bg px-4 py-2 text-[13px] text-apricot-fg md:mx-6">
          <span className="flex-1">{t('note.inTrash')}</span>
          <Button size={32} variant="secondary" onClick={() => action.mutate({ kind: 'restore' })}>
            {t('note.restore')}
          </Button>
        </div>
      )}
      {confirmDelete && (
        <div className="mx-4 flex items-center gap-3 rounded-tile bg-apricot-bg px-4 py-2 text-[13px] text-apricot-fg md:mx-6">
          <span className="flex-1">{t('note.deleteConfirm')}</span>
          <Button size={32} variant="ghost" onClick={() => setConfirmDelete(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            size={32}
            variant="danger"
            onClick={() =>
              action.mutate(
                { kind: 'delete' },
                {
                  onSuccess: () => {
                    toast(t('note.deleted'), {
                      action: { label: t('note.undo'), onClick: () => action.mutate({ kind: 'restore' }) },
                    })
                    void navigate(
                      note.containerId
                        ? { to: '/c/$containerId', params: { containerId: note.containerId } }
                        : { to: '/inbox' },
                    )
                  },
                },
              )
            }
          >
            {t('note.delete')}
          </Button>
        </div>
      )}

      <article className="mx-auto w-full max-w-[720px] px-5 pt-6 pb-24 md:px-6">
        {find && (
          <div className="sticky top-3 z-20 flex h-0 justify-end">
            <FindBar
              editor={editorHandle}
              seed={find.seed}
              focusKey={find.key}
              onClose={() => setFind(null)}
            />
          </div>
        )}
        <input
          ref={titleRef}
          value={title}
          disabled={!note.writable}
          onChange={(e) => {
            setTitle(e.target.value)
            saveTitle(e.target.value)
          }}
          placeholder={t('note.untitled')}
          aria-label={t('note.title')}
          className="w-full bg-transparent font-medium text-[30px] text-text-1 leading-tight outline-none placeholder:text-line-3"
        />
        <PropertiesHeader
          value={note.properties as Properties}
          disabled={!note.writable}
          onChange={(properties) => update.mutate({ properties })}
        />
        {note.type === 'brief' && briefLines(note.contentMd) > BRIEF_MAX_LINES && (
          <p className="mt-3 rounded-tile border border-apricot bg-apricot-bg px-3 py-2 text-[13px] text-apricot-fg">
            {t('note.briefTooLong', { count: briefLines(note.contentMd), max: BRIEF_MAX_LINES })}
          </p>
        )}
        <div ref={editorBox} className="mt-2 border-line-1 border-t pt-4">
          {editorReady ? (
            <Suspense fallback={<p className="text-[13px] text-meta">{t('editor.loading')}</p>}>
              <NoteEditor
                noteId={note.id}
                containerId={note.containerId}
                writable={note.writable && !deleted}
                user={{ id: me.id, name: me.name }}
                onStatus={setStatus}
                handleRef={editorHandle}
              />
            </Suspense>
          ) : (
            <div className="min-h-24" />
          )}
        </div>
        <NoteTasks noteId={note.id} className="mt-8" />
        <Subpages note={note} />
      </article>

      <ContextPanel>
        <Segmented
          aria-label={t('empty.context')}
          value={panel}
          onValueChange={setPanel}
          options={[
            { value: 'backlinks', label: t('backlinks.tab') },
            { value: 'versions', label: t('versions.tab') },
          ]}
        />
        {panel === 'backlinks' ? (
          <BacklinksPanel noteId={note.id} />
        ) : (
          <Suspense fallback={null}>
            <VersionsPanel noteId={note.id} currentMd={note.contentMd} writable={note.writable} />
          </Suspense>
        )}
      </ContextPanel>
    </div>
  )
}

/** Container › parent pages › page (ADR 0007). */
function Breadcrumbs({ note }: { note: NoteDetail }) {
  const { t } = useTranslation()
  return (
    <nav
      aria-label={t('pageTree.breadcrumb')}
      className="hidden min-w-0 items-center gap-1 text-[12px] text-meta md:flex"
    >
      {note.container ? (
        <Link
          to="/c/$containerId"
          params={{ containerId: note.container.id }}
          search={{ tab: 'notes' }}
          className="shrink-0 truncate hover:text-text-1"
        >
          {note.container.title}
        </Link>
      ) : (
        <Link to="/inbox" className="shrink-0 hover:text-text-1">
          {t('nav.inbox')}
        </Link>
      )}
      {note.ancestors.map((a) => (
        <span key={a.id} className="flex min-w-0 items-center gap-1">
          <ChevronRight size={12} className="shrink-0" />
          <Link to="/n/$noteId" params={{ noteId: a.id }} className="truncate hover:text-text-1">
            {a.title || t('note.untitled')}
          </Link>
        </span>
      ))}
      <ChevronRight size={12} className="shrink-0" />
      <span className="truncate text-text-2">{note.title || t('note.untitled')}</span>
      <span className="shrink-0">· {t(`noteType.${note.type}`)}</span>
    </nav>
  )
}

function Subpages({ note }: { note: NoteDetail }) {
  const { t } = useTranslation()
  const kids = useQuery({
    ...notesQuery({ parentNoteId: note.id, limit: 200 }),
    enabled: note.childCount > 0,
  })
  const createSub = useCreateSubpage()
  const items = note.childCount > 0 ? (kids.data?.items ?? []) : []
  if (note.deletedAt) return null
  return (
    <section className="mt-10 border-line-1 border-t pt-4">
      <div className="flex items-center justify-between">
        <h2 className="meta-label">{t('pageTree.subpages')}</h2>
        {note.writable && (
          <Button size={32} variant="ghost" onClick={() => createSub(note.id)}>
            <FilePlus size={15} strokeWidth={1.9} />
            {t('pageTree.addSubpage')}
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="mt-2 text-[13px] text-meta">{t('pageTree.noSubpages')}</p>
      ) : (
        <ul className="mt-2 flex flex-col">
          {items.map((c) => (
            <li key={c.id}>
              <Link
                to="/n/$noteId"
                params={{ noteId: c.id }}
                className="flex h-9 items-center gap-2 rounded-tile px-2 text-[14px] text-text-1 hover:bg-grey-2"
              >
                {c.icon ? (
                  <span className="w-4 text-center">{c.icon}</span>
                ) : (
                  <FileText size={15} strokeWidth={1.9} className="text-meta" />
                )}
                <span className="truncate">{c.title || t('note.untitled')}</span>
                {c.childCount > 0 && <span className="text-[11px] text-meta">{c.childCount}</span>}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function Item({
  icon: Icon,
  label,
  onSelect,
  danger,
}: {
  icon: typeof Pin
  label: string
  onSelect: () => void
  danger?: boolean
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className={cn(
        'flex h-9 cursor-pointer items-center gap-2.5 rounded-tile px-3 text-[13px] outline-none data-[highlighted]:bg-grey-2',
        danger ? 'text-apricot-fg' : 'text-text-1',
      )}
    >
      <Icon size={15} strokeWidth={1.9} />
      {label}
    </DropdownMenu.Item>
  )
}

function slugify(s: string) {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60)
}
